import { getGradient } from "../../DashboardPage/components/chartsPlagins"

export const RATE_SERIES = {
  CURRENT: "current",
  CURRENT_WITHOUT_US: "current_without_us",
  PREVIOUS: "previous",
  PREVIOUS_WITHOUT_US: "previous_without_us"
}

// Colour and dash are read by both the line and its legend entry, so they are spelled once here.
const SERIES_STYLE = {
  [RATE_SERIES.CURRENT]: { color: "#225AEA", dashed: false, label: "With us" },
  [RATE_SERIES.CURRENT_WITHOUT_US]: { color: "#A1A1AA", dashed: true, label: "Without us" },
  [RATE_SERIES.PREVIOUS]: { color: "#F79009", dashed: false, label: "Prev. with us" },
  [RATE_SERIES.PREVIOUS_WITHOUT_US]: { color: "#F79009", dashed: true, label: "Prev. without us" }
}

// The legend names exactly the series the chart is drawing, hidden ones included — a toggled-off
// series is still listed, that is how it is toggled back on.
export const getRateLegendItems = (chartData) =>
  (chartData?.datasets ?? [])
    .filter((dataset) => dataset.seriesId)
    .map(({ seriesId }) => ({ id: seriesId, ...SERIES_STYLE[seriesId] }))

const mapAtLabels = (targetLabels, sourceLabels, values) => {
  if (!values) return values
  const byLabel = new Map(sourceLabels.map((label, index) => [label, values[index]]))
  return targetLabels.map((label) => (byLabel.has(label) ? byLabel.get(label) : null))
}

// Both modes live in one `chart_type=rate` response: the endpoint converts `with_us`/`without_us`
// to percentages and sends the raw counts alongside them regardless, so Count is picked out of the
// same answer instead of asked for again.
const pickSeries = (result, chartType) =>
  chartType === "count"
    ? { withUs: result.with_us_count, withoutUs: result.without_us_count }
    : { withUs: result.with_us, withoutUs: result.without_us }

// Health's dataset field names are kept verbatim: this reuses Health's tooltip, which reads the
// series off the dataset rather than off the plotted points — so `with_us`/`without_us` there carry
// whichever unit the toggle is on, the same as the plotted line.
export const buildRateChartData = (overview, chartType, activeSeries) => {
  const isHidden = (seriesId) => Boolean(activeSeries) && !activeSeries.has(seriesId)

  const mainLabels = overview?.labels ?? []
  const comparison = overview?.comparison
  const comparisonLabels = comparison?.labels ?? []
  const mainResult = overview?.result ?? {}
  const comparisonResult = comparison?.result ?? {}
  const main = pickSeries(mainResult, chartType)
  const compared = pickSeries(comparisonResult, chartType)

  const mainLabelSet = new Set(mainLabels)
  // An overlapping comparison window needs a shared axis, or the two lines sit at the wrong dates.
  const hasOverlap =
    comparisonLabels.length > 0 && comparisonLabels.some((label) => mainLabelSet.has(label))

  let labels
  let withUs
  let withoutUs
  let comparisonWithUs
  let comparisonWithoutUs
  let alignedMainLabels
  let alignedComparisonLabels

  if (hasOverlap) {
    labels = Array.from(new Set([...mainLabels, ...comparisonLabels])).sort()
    withUs = mapAtLabels(labels, mainLabels, main.withUs)
    withoutUs = mapAtLabels(labels, mainLabels, main.withoutUs)
    comparisonWithUs = mapAtLabels(labels, comparisonLabels, compared.withUs)
    comparisonWithoutUs = mapAtLabels(labels, comparisonLabels, compared.withoutUs)
    alignedMainLabels = labels.map((label) => (mainLabelSet.has(label) ? label : null))
    alignedComparisonLabels = labels
  } else {
    labels = comparisonLabels.length > mainLabels.length ? comparisonLabels : mainLabels
    withUs = main.withUs
    withoutUs = main.withoutUs
    comparisonWithUs = compared.withUs
    comparisonWithoutUs = compared.withoutUs
    alignedMainLabels = labels.map((_, index) => mainLabels[index] ?? null)
    alignedComparisonLabels = labels.map((_, index) => comparisonLabels[index] ?? null)
  }

  const singlePoint = labels.length === 1
  const pointStyle = singlePoint
    ? { pointRadius: 4, pointHoverRadius: 6 }
    : { pointRadius: 0, pointHoverRadius: 0 }

  const datasets = [
    {
      seriesId: RATE_SERIES.CURRENT,
      hidden: isHidden(RATE_SERIES.CURRENT),
      label: chartType,
      data: withUs,
      selectType: chartType,
      with_us: main.withUs,
      without_us: main.withoutUs,
      with_us_count: mainResult.with_us_count,
      without_us_count: mainResult.without_us_count,
      with_us_volume: mainResult.with_us_volume,
      without_us_volume: mainResult.without_us_volume,
      main_labels: alignedMainLabels,
      comparison_labels: alignedComparisonLabels,
      comparison_label: overview?.comparison_period?.label,
      comparison_with_us: compared.withUs,
      comparison_without_us: compared.withoutUs,
      comparison_with_us_count: comparisonResult.with_us_count,
      comparison_without_us_count: comparisonResult.without_us_count,
      comparison_with_us_volume: comparisonResult.with_us_volume,
      comparison_without_us_volume: comparisonResult.without_us_volume,
      borderColor: SERIES_STYLE[RATE_SERIES.CURRENT].color,
      borderWidth: 2,
      yAxisID: "y",
      fill: true,
      backgroundColor: (context) => {
        const { ctx, chartArea } = context.chart
        if (!chartArea) return undefined
        return getGradient(ctx, chartArea)
      },
      ...pointStyle
    },
    {
      seriesId: RATE_SERIES.CURRENT_WITHOUT_US,
      hidden: isHidden(RATE_SERIES.CURRENT_WITHOUT_US),
      label: `${chartType} without us`,
      data: withoutUs,
      selectType: chartType,
      borderColor: SERIES_STYLE[RATE_SERIES.CURRENT_WITHOUT_US].color,
      borderWidth: 2,
      borderDash: [8, 6],
      yAxisID: "y",
      fill: false,
      ...pointStyle
    }
  ]

  if (comparisonWithUs?.length) {
    datasets.push({
      seriesId: RATE_SERIES.PREVIOUS,
      hidden: isHidden(RATE_SERIES.PREVIOUS),
      label: `${chartType} previous`,
      data: comparisonWithUs,
      borderColor: SERIES_STYLE[RATE_SERIES.PREVIOUS].color,
      borderWidth: 1.5,
      pointRadius: 0,
      pointHoverRadius: 0,
      yAxisID: "y",
      fill: false,
      order: 3
    })
  }

  if (comparisonWithoutUs?.length) {
    datasets.push({
      seriesId: RATE_SERIES.PREVIOUS_WITHOUT_US,
      hidden: isHidden(RATE_SERIES.PREVIOUS_WITHOUT_US),
      label: `${chartType} without us previous`,
      data: comparisonWithoutUs,
      borderColor: SERIES_STYLE[RATE_SERIES.PREVIOUS_WITHOUT_US].color,
      borderWidth: 1.5,
      borderDash: [8, 6],
      pointRadius: 0,
      pointHoverRadius: 0,
      yAxisID: "y",
      fill: false,
      order: 4
    })
  }

  return { labels, datasets }
}
